import { useState } from 'react';
import { getInsightForSource, TRANSFER_PATHS, PROGRAM_BOOKING_URLS, buildTransferSteps } from '../flightInsights';
import { useTripBuilder } from '../TripBuilderContext';
import bookingLinks from '../bookingLinks';

const SOURCE_LABELS = {
  amex_mr: 'Amex MR',
  chase_ur: 'Chase UR',
  capital_one: 'Capital One Venture',
  citi_ty: 'Citi ThankYou',
  bilt: 'Bilt',
};

const SOURCE_COLORS = {
  amex_mr: 'text-blue-300 border-blue-500/30 bg-blue-500/10',
  chase_ur: 'text-indigo-300 border-indigo-500/30 bg-indigo-500/10',
  capital_one: 'text-rose-300 border-rose-500/30 bg-rose-500/10',
  citi_ty: 'text-sky-300 border-sky-500/30 bg-sky-500/10',
  bilt: 'text-emerald-300 border-emerald-500/30 bg-emerald-500/10',
};

// Surfaced under each live flight result. Tells the user:
//   1. WHY this airline program is a recommendation (sweet spot, pros, cons)
//   2. WHAT the actual cabin product is like (lie-flat, layout, food)
//   3. HOW to convert points to this program — which currencies transfer in,
//      with their best-available ratio + transfer time, plus a link straight
//      to each transfer portal
//   4. The numbered transfer-then-book checklist
export default function FlightInsightCard({ source }) {
  const tb = useTripBuilder();
  const [open, setOpen] = useState(false);
  const insight = getInsightForSource(source);
  if (!insight) return null;

  const key = String(source || '').toLowerCase().replace(/[^a-z]/g, '');
  const programKey = key === 'airfrance' ? 'flyingblue'
    : key === 'britishairways' ? 'ba'
    : key === 'virgin' ? 'virginatlantic'
    : key === 'ms' ? 'turkish'
    : key;
  const paths = TRANSFER_PATHS[programKey] || [];
  const bookingUrl = PROGRAM_BOOKING_URLS[programKey];

  // Pick the user's best available source — first transfer path where they
  // actually have a positive balance.
  const balances = tb.userConfig?.pointsBalances || {};
  const bestPath = paths.find(p => Number(balances[p.source] || 0) > 0) || paths[0];
  const steps = bestPath ? buildTransferSteps(programKey, bestPath.source) : [];

  return (
    <div className="mt-2 border border-brand-ink/10 rounded-lg overflow-hidden bg-brand-ink/[0.03]">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full px-3 py-2 flex items-center justify-between text-left hover:bg-brand-ink/[0.05] transition"
      >
        <div className="flex items-center gap-2 text-xs">
          <span className="text-teal font-bold">{open ? '▾' : '▸'}</span>
          <span className="font-bold text-brand-ink/85">About this {insight.name} redemption + how to book it</span>
        </div>
        {bestPath && (
          <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${SOURCE_COLORS[bestPath.source] || ''}`}>
            Best from your {SOURCE_LABELS[bestPath.source] || bestPath.source} ({Number(balances[bestPath.source] || 0).toLocaleString()})
          </span>
        )}
      </button>

      {open && (
        <div className="px-3 pb-3 pt-1 space-y-3 text-xs">

          <div className="bg-teal/8 border border-teal/20 rounded-md p-2.5">
            <div className="font-bold text-teal uppercase tracking-wider text-[10px] mb-1">Why we recommend it</div>
            <div className="text-brand-ink/85 leading-snug">{insight.whyRecommend}</div>
          </div>

          <div>
            <div className="font-bold text-brand-ink/85 mb-1">Sweet spot</div>
            <div className="text-brand-ink/75 leading-snug">{insight.sweetSpot}</div>
          </div>

          <div className="grid sm:grid-cols-2 gap-2">
            <div className="bg-teal/5 border border-teal/15 rounded-md p-2">
              <div className="text-[10px] font-bold text-teal uppercase tracking-wider mb-1">Pros</div>
              <ul className="space-y-0.5 list-disc list-inside text-brand-ink/80 leading-snug">
                {insight.pros.map((p, i) => <li key={i}>{p}</li>)}
              </ul>
            </div>
            <div className="bg-rose-500/5 border border-rose-500/15 rounded-md p-2">
              <div className="text-[10px] font-bold text-rose-300 uppercase tracking-wider mb-1">Cons</div>
              <ul className="space-y-0.5 list-disc list-inside text-brand-ink/80 leading-snug">
                {insight.cons.map((c, i) => <li key={i}>{c}</li>)}
              </ul>
            </div>
          </div>

          {insight.productNotes && (
            <div>
              <div className="font-bold text-brand-ink/85 mb-1">The business class product</div>
              <div className="text-brand-ink/75 leading-snug">{insight.productNotes}</div>
            </div>
          )}

          {/* Transfer paths */}
          <div className="bg-navy-light border border-brand-ink/15 rounded-md p-2.5">
            <div className="font-bold text-brand-ink/85 mb-1.5">How to convert your points to {insight.name}</div>
            {paths.length === 0 ? (
              <div className="text-brand-ink/55 text-[11px]">Direct transfers from major currencies aren't available — check the airline's program page for cash + miles or partner-program routes.</div>
            ) : (
              <div className="space-y-1.5">
                {paths.map(p => {
                  const have = Number(balances[p.source] || 0);
                  const link = bookingLinks[p.source]?.transfer;
                  return (
                    <div key={p.source} className="flex items-center gap-2 text-[11px]">
                      <span className={`shrink-0 px-2 py-0.5 rounded font-bold border ${SOURCE_COLORS[p.source] || ''}`}>
                        {SOURCE_LABELS[p.source] || p.source}
                      </span>
                      <span className="text-brand-ink/75">{p.ratio} · {p.time}</span>
                      <span className="text-brand-ink/55 ml-auto shrink-0">
                        {have > 0 ? `You have ${have.toLocaleString()}` : 'No balance set'}
                      </span>
                      {link && (
                        <a href={link} target="_blank" rel="noopener noreferrer" className="text-teal hover:underline shrink-0">
                          Open portal ↗
                        </a>
                      )}
                      {p.notes && <span className="basis-full text-[10px] text-amber-300/85">{p.notes}</span>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Step-by-step */}
          {steps.length > 0 && (
            <div>
              <div className="font-bold text-brand-ink/85 mb-1">Step-by-step</div>
              <ol className="space-y-1 text-brand-ink/80">
                {steps.map((s, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-teal font-bold shrink-0">{i + 1}.</span>
                    <span className="leading-snug">{s}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {bookingUrl && (
            <a
              href={bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-teal font-bold hover:underline"
            >
              Open {insight.name} booking site ↗
            </a>
          )}

        </div>
      )}
    </div>
  );
}
